<template>
    <div class="award-settings-container">
        <v-card class="mb-4">
            <v-card-title class="d-flex align-center py-4">
                <div class="d-flex align-center">
                    <v-icon left color="primary" size="24" class="mr-2">mdi-cog</v-icon>
                    <span class="text-h6">Award Settings</span>
                </div>
                <v-spacer></v-spacer>
                <v-btn
                    color="primary"
                    class="save-btn py-4 px-6"
                    :loading="saving"
                    :disabled="saving"
                    @click="saveSettings"
                >
                    <v-icon left size="24">mdi-content-save</v-icon>
                    Save Settings
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-4">
                <v-row>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="settings.awardRule"
                            :items="awardRuleOptions"
                            label="Default Award Rule"
                            outlined
                            class="settings-select"
                            hint="Select how many athletes to award by default"
                            persistent-hint
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field
                            v-if="settings.awardRule === 'custom'"
                            v-model.number="settings.customAwardCount"
                            type="number"
                            label="Custom Award Count"
                            outlined
                            class="settings-input"
                            min="1"
                            :rules="[(v) => !!v || 'Value is required', (v) => v > 0 || 'Value must be greater than 0']"
                        ></v-text-field>
                    </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>

                <div v-if="categories.length > 0" class="override-container">
                    <div class="d-flex align-center flex-wrap mb-4">
                        <h3 class="text-subtitle-1 font-weight-bold">Division Overrides</h3>
                        <v-spacer></v-spacer>
                        <v-switch
                            v-model="showOverrideControls"
                            label="Show Override Controls"
                            hide-details
                            class="switch-control"
                        ></v-switch>
                    </div>

                    <p v-if="showOverrideControls" class="text-body-2 mb-3">
                        Configure specific award rules for individual divisions. These settings will override the global
                        rule.
                    </p>

                    <v-data-table
                        v-if="showOverrideControls"
                        :headers="overrideHeaders"
                        :items="categories"
                        :items-per-page="10"
                        class="elevation-1 override-table"
                    >
                        <template #item.division="{ item }">
                            {{ item.description() }}
                        </template>

                        <template #item.rule="{ item }">
                            <v-select
                                v-model="overrides[item.id]"
                                :items="awardRuleOptions"
                                hide-details
                                outlined
                                class="override-select"
                                @change="overrideChanged(item.id)"
                            ></v-select>
                        </template>

                        <template #item.custom="{ item }">
                            <v-text-field
                                v-if="overrides[item.id] === 'custom'"
                                v-model.number="customCounts[item.id]"
                                type="number"
                                hide-details
                                outlined
                                min="1"
                                class="custom-count-field"
                                @change="customCountChanged(item.id)"
                            ></v-text-field>
                            <span v-else>-</span>
                        </template>

                        <template #item.athletes="{ item }">
                            {{ item.results?.length || 0 }}
                        </template>

                        <template #item.awards="{ item }">
                            <v-chip small :color="getAwardCountColor(getAwardCount(item))">
                                {{ getAwardCount(item) }}
                            </v-chip>
                        </template>

                        <template #item.reset="{ item }">
                            <v-btn
                                v-if="overrides[item.id]"
                                icon
                                color="error"
                                class="reset-btn"
                                @click="resetOverride(item.id)"
                            >
                                <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                </div>
            </v-card-text>
        </v-card>

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" bottom>
            {{ snackbar.text }}
            <template #action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar.show = false" class="pa-3">
                    <v-icon left>mdi-close</v-icon>
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import Meet from '@/Models/Meet.js';

export default {
    name: 'AwardSettings',

    props: {
        meetId: {
            type: Number,
            required: true,
        },
        categories: {
            type: Array,
            default: () => [],
        },
    },

    data: () => ({
        saving: false,
        settings: {
            awardRule: 'top3',
            customAwardCount: 3,
        },
        overrides: {},
        customCounts: {},
        showOverrideControls: false,
        awardRuleOptions: [
            { text: 'Top 3', value: 'top3' },
            { text: 'Top 50% + 1', value: 'top50plus1' },
            { text: 'Top 6', value: 'top6' },
            { text: 'Top 8', value: 'top8' },
            { text: 'Top 10', value: 'top10' },
            { text: 'All Athletes', value: 'all' },
            { text: 'Custom Count', value: 'custom' },
        ],
        overrideHeaders: [
            { text: 'Division', value: 'division', sortable: true },
            { text: 'Award Rule', value: 'rule', sortable: false, width: '170px' },
            { text: 'Custom Count', value: 'custom', sortable: false, width: '120px' },
            { text: 'Athletes', value: 'athletes', sortable: true, width: '100px' },
            { text: 'Awards', value: 'awards', sortable: true, width: '100px' },
            { text: '', value: 'reset', sortable: false, width: '50px' },
        ],
        snackbar: {
            show: false,
            text: '',
            color: 'success',
        },
    }),

    created() {
        this.loadSettings();
    },

    methods: {
        async loadSettings() {
            try {
                const { data } = await axios.get(`/api/meets/${this.meetId}/award-settings`);

                if (data.settings) {
                    this.settings = {
                        awardRule: data.settings.award_rule || 'top3',
                        customAwardCount: data.settings.custom_award_count || 3,
                    };

                    // Load division overrides
                    if (data.overrides && data.overrides.length) {
                        data.overrides.forEach((override) => {
                            this.overrides[override.result_category_id] = override.award_rule;
                            if (override.award_rule === 'custom') {
                                this.customCounts[override.result_category_id] = override.custom_award_count;
                            }
                        });
                    }
                }
            } catch (error) {
                console.error('Error loading award settings:', error);
                this.showError('Could not load award settings');
            }
        },

        async saveSettings() {
            this.saving = true;

            try {
                // Prepare overrides data
                const divisionOverrides = Object.keys(this.overrides).map((categoryId) => ({
                    result_category_id: categoryId,
                    award_rule: this.overrides[categoryId],
                    custom_award_count: this.overrides[categoryId] === 'custom' ? this.customCounts[categoryId] : null,
                }));

                await Meet.api().post(`/api/meets/${this.meetId}/award-settings`, {
                    settings: {
                        award_rule: this.settings.awardRule,
                        custom_award_count:
                            this.settings.awardRule === 'custom' ? this.settings.customAwardCount : null,
                    },
                    overrides: divisionOverrides,
                });

                this.showSuccess('Award settings saved successfully');
            } catch (error) {
                console.error('Error saving award settings:', error);
                this.showError('Could not save award settings');
            } finally {
                this.saving = false;
            }
        },

        overrideChanged(categoryId) {
            // Initialize custom count if not set
            if (this.overrides[categoryId] === 'custom' && !this.customCounts[categoryId]) {
                this.customCounts[categoryId] = 3;
            }
        },

        customCountChanged(categoryId) {
            // Ensure value is at least 1
            if (this.customCounts[categoryId] < 1) {
                this.customCounts[categoryId] = 1;
            }
        },

        resetOverride(categoryId) {
            this.$delete(this.overrides, categoryId);
            this.$delete(this.customCounts, categoryId);
        },

        getAwardCount(category) {
            const totalAthletes = category.results?.length || 0;

            // Check if there's an override for this category
            const rule = this.overrides[category.id] || this.settings.awardRule;

            return this.calculateAwardCount(
                rule,
                totalAthletes,
                rule === 'custom'
                    ? this.overrides[category.id]
                        ? this.customCounts[category.id]
                        : this.settings.customAwardCount
                    : null,
            );
        },

        calculateAwardCount(rule, totalAthletes, customCount = null) {
            if (totalAthletes === 0) return 0;

            switch (rule) {
                case 'top3':
                    return Math.min(3, totalAthletes);
                case 'top50plus1':
                    return Math.min(Math.ceil(totalAthletes / 2) + 1, totalAthletes);
                case 'top6':
                    return Math.min(6, totalAthletes);
                case 'top8':
                    return Math.min(8, totalAthletes);
                case 'top10':
                    return Math.min(10, totalAthletes);
                case 'all':
                    return totalAthletes;
                case 'custom':
                    return Math.min(customCount || 3, totalAthletes);
                default:
                    return Math.min(3, totalAthletes);
            }
        },

        getAwardCountColor(count) {
            if (count >= 10) return 'purple';
            if (count >= 8) return 'indigo';
            if (count >= 6) return 'blue';
            if (count >= 3) return 'green';
            if (count > 0) return 'amber';
            return 'grey';
        },

        showSuccess(message) {
            this.snackbar = {
                show: true,
                text: message,
                color: 'success',
            };
        },

        showError(message) {
            this.snackbar = {
                show: true,
                text: message,
                color: 'error',
            };
        },
    },
};
</script>

<style scoped>
.award-settings-container {
    padding: 8px;
}

.settings-select ::v-deep .v-input__slot,
.settings-input ::v-deep .v-input__slot {
    min-height: 56px;
}

.override-select {
    min-width: 150px;
}

.override-select ::v-deep .v-select__selections {
    padding: 12px 0;
}

.custom-count-field {
    max-width: 100px;
}

.custom-count-field ::v-deep .v-input__slot {
    min-height: 46px;
}

.switch-control ::v-deep .v-input--selection-controls__ripple {
    transform: scale(1.5);
}

.switch-control ::v-deep .v-input--selection-controls__input {
    transform: scale(1.25);
}

.save-btn {
    min-width: 150px;
    height: 48px;
}

.reset-btn {
    width: 48px;
    height: 48px;
}

.override-table {
    margin-top: 16px;
}

.override-table ::v-deep tbody tr {
    height: 60px !important;
}

.override-table ::v-deep tbody td {
    padding: 0 12px;
}

/* iPad specific optimizations */
@media only screen and (min-width: 768px) and (max-width: 1024px) {
    .award-settings-container {
        padding: 16px;
    }

    .override-table ::v-deep tbody tr {
        height: 72px !important;
    }

    .override-table ::v-deep th {
        font-size: 0.95rem;
    }

    .override-table ::v-deep td {
        font-size: 1rem;
    }
}

/* iPad landscape orientation */
@media only screen and (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    .override-container {
        max-height: calc(100vh - 300px);
        overflow-y: auto;
    }
}

/* iPad portrait orientation */
@media only screen and (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    .save-btn {
        margin-top: 8px;
    }

    .award-settings-container {
        padding: 12px 8px;
    }

    .override-select {
        min-width: 120px;
    }
}
</style>
